import axios from 'axios';
import { msalInstance, initializeMsal, installApiAuth } from './apiAuth';
import type {
  Training,
  Module,
  AssessmentData,
  UserAnswer,
  AssessmentResult,
  TrainingMaster,
  ParticipantTrainingStatus,
  AssessmentQuestion,
  LeaderboardEntry
} from '../types/training';


// Create axios instance for our new API Backend (Azure Functions)
export const apiBackend = axios.create({
  baseURL: '/api'
});

// Sign-in token on every API request (specs/2026-10-30-secure-managed-functions.md)
installApiAuth(apiBackend);

// The self-reported email is only used by the API in report mode, as a
// fallback when there is no valid token. Remove once API_AUTH_MODE=enforce.
apiBackend.interceptors.request.use(async (config) => {
  try {
    const email = await getCurrentUserEmail();
    if (email) {
      // Must NOT use an x-ms- prefixed header name - Azure's production edge
      // layer reserves that prefix and strips any client-supplied header
      // using it before the request reaches the Function (see
      // specs/2026-09-15-fix-reserved-header-401.md). This survives to prod.
      config.headers['X-User-Email'] = email;
    }
  } catch (error) {
    console.warn('⚠️ Could not get user email for API request:', error);
  }
  return config;
});

// Add interceptor to track total tokens used by the portal
apiBackend.interceptors.response.use((response) => {
  console.log("⚡ [DEBUG] API Backend Interceptor Fired! Received payload from:", response.config?.url);
  try {
    const tokens = response.data?.usage?.total_tokens || response.data?.totalTokens || response.data?.tokens;
    if (tokens) {
      (window as any).totalPortalTokens = ((window as any).totalPortalTokens || 0) + tokens;
      console.log(`🤖 AI Tokens Used in this request: ${tokens}`);
      console.log(`📊 Cumulative Portal Tokens Used: ${(window as any).totalPortalTokens}`);
    } else {
      console.log("⚡ [DEBUG] Tokens field was not found in response payload:", response.data);
    }
  } catch (err) {
    console.warn("Could not parse token usage from response", err);
  }
  return response;
}, (error) => {
  return Promise.reject(error);
});

// Get current user email from MSAL
export const getCurrentUserEmail = async (): Promise<string> => {
  await initializeMsal();
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    return accounts[0].username || 'user@company.com';
  }
  return 'user@company.com';
};

// --- HELPER FUNCTIONS ---
const cleanModuleId = (id: string): string => {
  if (!id) return id;
  // Remove description after '[' or ':'
  return id.split('[')[0].split(':')[0].trim();
};

// --- PARTICIPANT FUNCTIONS (NOW CALLING AZURE FUNCTIONS API) ---

// Get assigned trainings for current user
export const getAssignedTrainings = async (): Promise<Training[]> => {
  try {
    const response = await apiBackend.get('/dashboard-data');
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      console.error('❌ API 500 Error Details:', error.response.data);
    }
    console.error('❌ Error fetching trainings from API:', error);
    throw error;
  }
};

// Get modules for a training
export const getTrainingModules = async (trainingId: string): Promise<Module[]> => {
  try {
    const response = await apiBackend.get(`/modules?trainingId=${encodeURIComponent(trainingId)}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching modules from API:', error);
    throw error;
  }
};

// Get assessment questions for a specific module
export const getModuleAssessment = async (
  trainingId: string,
  moduleId: string,
  quizId: number = 0
): Promise<AssessmentData> => {
  try {
    const quizParam = quizId > 0 ? `&quizId=${quizId}` : '';
    const response = await apiBackend.get(`/assessment?trainingId=${encodeURIComponent(trainingId)}&moduleId=${encodeURIComponent(cleanModuleId(moduleId))}${quizParam}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching assessment from API:', error);
    throw error;
  }
};

// Submit assessment answers
export const submitAssessment = async (
  trainingId: string,
  moduleId: string,
  answers: UserAnswer[],
  terminationReason?: string,
  fullscreenViolation?: any,
  quizId: number = 0
): Promise<AssessmentResult> => {
  try {
    const response = await apiBackend.post('/submit', {
      trainingId,
      moduleId: cleanModuleId(moduleId),
      quizId,
      answers,
      terminationReason,
      fullscreenViolation
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error submitting assessment to API:', error);
    throw error;
  }
};

// --- SUBJECTIVE ASSESSMENT FUNCTIONS ---

// Get subjective assessment questions for a specific module
export const getModuleAssessmentSubjective = async (
  trainingId: string,
  moduleId: string,
  quizId: number = 0
): Promise<AssessmentData> => {
  try {
    const quizParam = quizId > 0 ? `&quizId=${quizId}` : '';
    const response = await apiBackend.get(`/assessmentSubjective?trainingId=${encodeURIComponent(trainingId)}&moduleId=${encodeURIComponent(cleanModuleId(moduleId))}${quizParam}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching subjective assessment from API:', error);
    throw error;
  }
};

// Grade a single subjective question in the background
export const gradeSingleAssessmentSubjective = async (
  trainingId: string,
  moduleId: string,
  questionId: string,
  userAnswerText: string,
  officialAnswer: string = "",
  questionText: string = "",
  codeSnippet: string | null = null,
  quizId: number = 0
): Promise<{ score: number }> => {
  try {
    const response = await apiBackend.post('/gradeSingleSubjective', {
      trainingId,
      moduleId,
      quizId,
      questionId,
      userAnswerText,
      officialAnswer,
      questionText,
      codeSnippet
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error grading single subjective assessment question:', error);
    throw error;
  }
};

// Submit final pre-calculated scores
export const submitAssessmentSubjective = async (
  trainingId: string,
  moduleId: string,
  answers: (UserAnswer & { aiScore?: number })[],
  terminationReason?: string,
  fullscreenViolation?: any,
  quizId: number = 0
): Promise<AssessmentResult> => {
  try {
    const response = await apiBackend.post('/submitSubjective', {
      trainingId,
      moduleId: cleanModuleId(moduleId),
      quizId,
      answers,
      terminationReason,
      fullscreenViolation
    });
    return response.data;
  } catch (error) {
    console.error('❌ Error submitting subjective assessment:', error);
    throw error;
  }
};

// Enroll in an Open training
export const enrollInTraining = async (trainingId: string): Promise<void> => {
  try {
    await apiBackend.post('/enroll', { trainingId });
  } catch (error) {
    console.error('❌ Error enrolling in training:', error);
    throw error;
  }
};

// Request access to a Requestable training
export const requestTrainingAccess = async (trainingId: string): Promise<void> => {
  try {
    await apiBackend.post('/request-access', { trainingId });
  } catch (error) {
    console.error('❌ Error requesting access:', error);
    throw error;
  }
};

// Withdraw from a training
export const withdrawFromTraining = async (trainingId: string): Promise<void> => {
  try {
    await apiBackend.post('/withdraw', { trainingId });
  } catch (error) {
    console.error('❌ Error withdrawing from training:', error);
    throw error;
  }
};

// Get completed assessments
export const getCompletedAssessments = async (): Promise<Training[]> => {
  try {
    const trainings = await getAssignedTrainings();
    return trainings.filter(t => t.status === 'Successfully Completed');
  } catch (error) {
    console.error('❌ Error fetching completed assessments:', error);
    throw error;
  }
};

// Get leaderboard for an AI-led training (quiz-based)
export const getTrainingLeaderboard = async (trainingId: string): Promise<LeaderboardEntry[]> => {
  try {
    const response = await apiBackend.get(`/leaderboard?trainingId=${encodeURIComponent(trainingId)}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching leaderboard from API:', error);
    return [];
  }
};

// Get leaderboard for a trainer-led training (credits-based)
export const getTrainerLedLeaderboard = async (courseId: number): Promise<LeaderboardEntry[]> => {
  try {
    const response = await apiBackend.get(`/leaderboard?type=credits&courseId=${courseId}`);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching trainer-led leaderboard from API:', error);
    return [];
  }
};
